<template>
  <v-layout>
    <v-flex>
      <v-row align="center" no-gutters>
        <v-col class="text-left ml-n6" cols="12" md="6">
          <v-breadcrumbs :items="subNavs"></v-breadcrumbs>
        </v-col>
        <v-col class="text-right"  cols="12" md="6">
          <ActionButtons
            @doClose="doClose"
            @doPrint="doPrint"
            :actionBtnSettings="actionBtnSettings"
          >
          </ActionButtons>
        </v-col>
      </v-row>

      <v-card class="pa-6 mt-3">
        <v-card-title>
          Invoice
          <v-spacer></v-spacer>
          <InvoiceStatus :status="invoice.status" :statuses="statuses"></InvoiceStatus>
        </v-card-title>
        <v-card-subtitle>
          #{{invoice.invoice_no}}
          <v-spacer></v-spacer>
        </v-card-subtitle>
        <v-card-text class="text--primary mt-6">

          <v-row no-gutters>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-account-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_name}}</v-list-item-title>
                        <v-list-item-subtitle>Bill To</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-map-marker-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_address}}</v-list-item-title>
                        <v-list-item-subtitle>Address</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-phone-classic
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.bill_to_contact_no}}</v-list-item-title>
                        <v-list-item-subtitle>Contact No.</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-receipt-text-outline
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{invoice.ref_no ? invoice.ref_no : '-'}}</v-list-item-title>
                        <v-list-item-subtitle>Reference No.</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ invoice.invoice_date | moment("MMM D, YYYY") }}</v-list-item-title>
                        <v-list-item-subtitle>Invoice Date</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="12">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon>
                          mdi-calendar
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ invoice.due_date | moment("MMM D, YYYY") }}</v-list-item-title>
                        <v-list-item-subtitle>Due Date</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-data-table
            class="mt-10"
            :headers="headers"
            :items="items"
            :loading="loading"
            :items-per-page="1000"
            hide-default-footer
            loading-text="Loading Items..."
            no-data-text="No Items"
            ref="table">

            <template v-slot:[`item.description`]="{ item }">
              <div v-if="item.container" class="font-weight-bold">
                {{ item.container }}
              </div>
              <v-icon
                v-if="item.shipment_type"
                size="24"
                class="mr-2">
                {{ getShipmentIcon(item) }}
              </v-icon>
              {{ item.description }}
            </template>

            <template v-slot:[`item.measure`]="{ item }">
              <div v-if="item.shipment_type">
                <span v-if="isShipmentForSea(item.shipment_type)">{{formatNumber(item.measure) }} CBM</span>
                <span v-if="!isShipmentForSea(item.shipment_type)">{{formatNumber(item.measure) }} KG</span>
              </div>
            </template>

            <template v-slot:[`item.qty`]="{ item }">
              {{ formatNumber(item.qty) }}
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              {{ formatNumberAmount($numeral, item.amount) }}
            </template>


          </v-data-table>
          <v-divider></v-divider>

          <v-row class="mt-6">
            <v-col cols="12" sm="12" md="5">
              <div class="text-subtitle-2" v-if="invoice.remarks">
                Remarks
                <br>
                <span class="text-caption">{{invoice.remarks}}</span>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="3" offset-md="4">
              <v-card-title class="text-caption pa-0">
                Subtotal
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">{{formatNumberAmount($numeral, invoice.subtotal)}}</span>
              </v-card-title>
              <v-card-title class="text-caption pa-0 py-4">
                Discounts
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">-{{formatNumberAmount($numeral, invoice.discounts)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 pb-4">
                Add VAT 12%
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">{{formatNumberAmount($numeral, invoice.add_vat_amount)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 pb-4">
                Less Withholding Tax
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">-{{formatNumberAmount($numeral, invoice.less_withholding_tax_amount)}}</span>
              </v-card-title>

              <v-card-title class="text-caption pa-0 pb-4">
                <strong>Points Redeemed</strong>
                <v-spacer></v-spacer>
                <span class="text-subtitle-2">{{formatNumberAmount($numeral, invoice.points_redeemed)}}</span>
              </v-card-title>
              <v-card-title class="text-caption pa-0">
                Grand Total
                <v-spacer></v-spacer>
                <span class="text-subtitle-1 font-weight-bold">{{formatNumberAmount($numeral, grandTotal)}}</span>
              </v-card-title>

            </v-col>
          </v-row>
        </v-card-text>

        <v-card-title class="mt-12">
          Proof of Payments
        </v-card-title>
        <v-card-text>
          <v-row class="mt-6">
            <v-col
              v-for="img in invoiceImages"
              :key="img.id"
              class="d-flex child-flex"
              cols="4"
            >
              <v-img
                :src="img.loc"
                :lazy-src="img.loc"
                aspect-ratio="1"
                class="grey lighten-2"
              >
                <div class="float-right mr-2 mt-2 mb-2">
                  <v-btn
                    class="mr-2"
                    color="grey"
                    fab
                    small
                    dark
                    depressed
                    @click="showImg(img.loc)"
                  >
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>
                </div>
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-col>
          </v-row>
        </v-card-text>

      </v-card>



      <SnackBar :snackbarDetails="snackbarDetails"></SnackBar>
      <Loader :loading="loading"></Loader>
    </v-flex>
  </v-layout>
</template>

<script>
  import SnackBar from '@/components/snackbar/SnackBar.vue'
  import Loader from '@/components/Loader.vue'
  import { mapGetters } from "vuex";
  import ActionButtons from '@/components/ActionButtons.vue'
  import InvoiceStatus from '@/components/InvoiceStatus.vue'

  export default {
    name: 'InvoiceViewPage',
    data: () => ({
      id: null,
      adminNamespace: 'admin',
      loading: false,
      print: false,
      isLoading: false,
      deletedItems: [],
      perPage: 2000,
      actionBtnSettings: {
        edit: 0,
        save: 0,
        refresh: 0,
        close: 1,
        print: 1,
        send: 0,
        save_and_print: 0,
        save_disabled: false,
        close_disabled: false,
        print_disabled: false,
        save_and_print_disabled: false
      },
      snackbarDetails: {
        color: 'sb-success',
        msg: '',
        show: false
      },
      invoice: {
        id: '',
        invoice_no: '',
        ref_no: '',
        bill_to_name: '',
        bill_to_contact_no: '',
        bill_to_address: '',
        invoice_date: '',
        remarks: '',
        sub_total: 0,
        discounts: 0,
        other_charges: 0,
        net_amount: 0
      },
      statuses: [],
      invoiceImages: [],
      subNavs: [
        {
          text: 'Invoices',
          disabled: false,
          exact: true,
          to: {
            name: 'invoices'
          },
        },
        {
          text: 'View Invoice',
          disabled: true,
          href: 'breadcrumbs_link_2',
        },
      ],
      items: [],
      headers: [
        {
          text: 'Item',
          align: 'start',
          value: 'description',
          sortable: false
        },
        {
          text: 'Qty',
          align: 'center',
          value: 'qty',
          sortable: false,
          width: 180
        },
        {
          text: 'Measure',
          align: 'center',
          value: 'measure',
          sortable: false,
          width: 180
        },
        {
          text: 'Rate',
          align: 'center',
          value: 'rate',
          sortable: false,
          width: 180
        },
        {
          text: 'Amount',
          align: 'right',
          value: 'amount',
          sortable: false,
          width: 180
        }
      ]
    }),
    watch: {
    },
    computed: {
      ...mapGetters(['formatNumberAmount', 'formatNumber', 'formatNumberAmountNoComma', 'getCurrentUser', 'forSea']),
      grandTotal: function(){
        return Number(this.formatNumberAmountNoComma(this.$numeral, this.invoice.grand_total))  - Number(this.formatNumberAmountNoComma(this.$numeral, this.invoice.points_redeemed))
      }
    },
    methods: {
      getShipmentIcon (item) {
        if(item.shipment_type){
          return this.isShipmentForSea(item.shipment_type) ? 'mdi-ferry' : 'mdi-airplane'
        }else{
          return ''
        }
      },
      isShipmentForSea (shipment_type){
        return this.forSea(shipment_type, this)
      },
      doClose (){
        this.$router.push({ name: 'invoices' })
      },
      doConfirm () {
        this.dialog.show = false
        if(this.dialog.type == 'confirm-payment'){
          this.paid()
        }
      },
      doCancel () {
        this.dialog.show = false
      },
      doPrint (){
        this.print = true
        this.getInvoice()
      },
      getInvoice () {
        if(this.print){
          const currentDate = new Date();
          const unixTimestamp = Math.floor(currentDate.getTime() / 1000)

          window.open('/api/'+this.adminNamespace+'/invoices/'+this.id+'?print=true&v='+unixTimestamp, '_newtab');
          return false
        }
        let self = this
        this.loading = true
        this.$http.plain.get(this.adminNamespace+'/invoices/'+this.id)
          .then(response => {
            if(!this.print){
              this.invoice = response.data
              this.items = this.invoice.items
            }
          })
          .catch(error => {
            console.log(error);
          })
          .then(function () {
            self.loading = false
            this.print
          })
      },
      showImg (imgLoc) {
        window.open(imgLoc, '_newtab');
      },
      getInvoiceImages () {
        let self = this
        this.$http.plain.get(this.adminNamespace+'/invoices/confirmation-images/'+this.id)
          .then(response => {
            this.invoiceImages = response.data
          })
          .catch(error => {
            console.log(error);
          })
      },
      getDefaultData () {
        let self = this
        this.$http.plain.get('/default-data?invoice_statuses=true')
          .then(response => {
            this.statuses = response.data.invoice_statuses
          })
          .catch(error => {
            console.log(error);
          })
      },
      showMessage(msg, color){
        this.snackbarDetails = {
          color: color,
          msg: msg,
          show: true
        }
      }
    },
    mounted() {
      if(this.$route.params.id != 'add'){
        this.id = this.$route.params.id
      }
      this.getDefaultData()
      this.getInvoice()
      this.getInvoiceImages()
    },
    components: {
      SnackBar,
      Loader,
      ActionButtons,
      InvoiceStatus
    }
  }
</script>
